import { FC, useRef } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import {
  Box,
  Divider,
  InputGroup,
  InputRightElement,
  Skeleton,
  VStack,
} from '@chakra-ui/react';
/* eslint-disable import/no-extraneous-dependencies */
import { useSize } from '@chakra-ui/react-use-size';
import { zodResolver } from '@hookform/resolvers/zod';

import { InternationalTransferCurrency } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useCompanyUserId from 'common/hooks/useCompanyUserId/useCompanyUserId';
import useCurrentLanguage from 'common/hooks/useCurrentLanguage';
import useDebouncedCallback from 'common/hooks/useDebouncedCallback';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Text from 'components/_core/Text';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import NavigationHeader from 'features/Payments/Pages/Transfers/components/NavigationHeader';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import * as ContentLayout from 'layouts/ContentLayout';
import * as Layout from 'layouts/StepperLayout';

import useBankTransferData from '../../hooks/useBankTransferData';
import TransferTypeCard from '../TransferDetails/TransferTypeCard';
import { parseAmount } from '../TransferDetails/utils';
import CurrencyInput from './CurrencyInput';
import {
  useCreateInternationalTransferQuote,
  UseInternationalTransferQuoteReturnType,
} from './hooks/useCreateInternationalTransferQuote';
import { useInternationalTransferCurrencies } from './hooks/useInternationalTransferCurrencies';
import locales from './locales';
import {
  getInternationalTransferAmountAndCurrencyFormSchema,
  InternationalTransferAmountAndCurrencyFormSchema,
} from './selectAmountAndCurrency.schema';
import { formatAmountWithDecimals } from './utils/formatAmountWithDecimals';

type SelectAmountAndCurrencyProps = StepProps<BankTransferCreationStep>;

const NOT_NUMBER_NOR_COMMA_NOR_DOT_REGEX = /[^\d.,]/g;
type CurrencyOption = Pick<
  InternationalTransferCurrency,
  'code' | 'name' | 'isoCountryCode' | 'supportsDecimals'
>;

type Currency = Pick<
  InternationalTransferCurrency,
  'code' | 'name' | 'isoCountryCode' | 'supportsDecimals'
>;

interface AmountAndCurrencyFormProps {
  currencies: Currency[];
  nextBalanceFloat: number;
  onSubmit: (values: {
    currency: Currency;
    quote: UseInternationalTransferQuoteReturnType['quote'];
  }) => void;
}

const AmountAndCurrencyForm: FC<AmountAndCurrencyFormProps> = ({
  currencies,
  nextBalanceFloat,
  onSubmit,
}) => {
  const currentLanguage = useCurrentLanguage();
  const { createQuote, isCreatingQuote, quote } =
    useCreateInternationalTransferQuote();
  const { companyProfileId } = useCompanyContext();
  const amountInputRef = useRef<HTMLInputElement>(null);
  const { width } = useSize(amountInputRef) || { width: 0 };

  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is required in InternationalTransferAmountAndCurrencySelection',
    );
  }

  const { companyUserId } = useCompanyUserId();
  if (!companyUserId) {
    throw new Error(
      'companyUserId is required in InternationalTransferAmountAndCurrencySelection',
    );
  }

  const { clearErrors, control, formState, getValues, handleSubmit, setError } =
    useForm<InternationalTransferAmountAndCurrencyFormSchema>({
      defaultValues: {
        amount: '',
        currency:
          currencies.find(({ code }) => code === 'USD') ?? currencies[0],
      },
      resolver: zodResolver(
        getInternationalTransferAmountAndCurrencyFormSchema({
          lang: currentLanguage,
          validateBalance: () => {
            if (!quote) {
              return false;
            }
            return nextBalanceFloat > quote.source.amountFloat;
          },
        }),
      ),
      /**
       * The form validations include the balance validation which can only be
       * done after the quote is created. The quote is created when the user
       * types the amount and the currency. The reValidateMode is set to 'onSubmit'
       * to avoid the balance validation to be triggered on every keystroke, and
       * let the user preview the quote before telling them that the balance is
       * insufficient.
       */
      reValidateMode: 'onSubmit',
    });

  const createQuoteDebounced = useDebouncedCallback(
    async (amountFloat: number) => {
      if (
        quote?.target.amountFloat === amountFloat &&
        quote.target.currency === getValues('currency').code
      ) {
        return;
      }

      clearErrors('amount');
      const { errors } = await createQuote({
        variables: {
          input: {
            amountFloat,
            companyProfileId,
            companyUserId,
            currency: getValues('currency').code,
          },
          lang: currentLanguage,
        },
      });
      if (errors?.[0]) {
        setError('amount', {
          message: errors[0].message,
          type: 'custom',
        });
      }
    },
    500,
  );

  const handleAmountChange = ({
    field,
    value,
  }: {
    value: string;
    field: ControllerRenderProps<
      InternationalTransferAmountAndCurrencyFormSchema,
      'amount'
    >;
  }) => {
    const sanitizedValue = value.replace(
      NOT_NUMBER_NOR_COMMA_NOR_DOT_REGEX,
      '',
    );
    field.onChange(sanitizedValue);
    const parsedAmount = parseAmount(sanitizedValue, currentLanguage);
    if (!parsedAmount || parsedAmount <= 0) {
      return;
    }
    createQuoteDebounced(parsedAmount);
  };

  const handleAmountBlur = (
    field: ControllerRenderProps<
      InternationalTransferAmountAndCurrencyFormSchema,
      'amount'
    >,
  ) => {
    if (!field.value && !quote) {
      return;
    }
    const { formattedAmount } = formatAmountWithDecimals({
      amount: field.value,
      lang: currentLanguage,
      withDecimals: getValues('currency').supportsDecimals,
    });
    field.onChange(formattedAmount);
  };

  const handleCurrencyChange = ({
    amountField,
    currencyField,
    selectedCurrency,
  }: {
    amountField: ControllerRenderProps<
      InternationalTransferAmountAndCurrencyFormSchema,
      'amount'
    >;
    currencyField: ControllerRenderProps<
      InternationalTransferAmountAndCurrencyFormSchema,
      'currency'
    >;
    selectedCurrency: CurrencyOption;
  }) => {
    if (!selectedCurrency) {
      return;
    }

    currencyField.onChange(selectedCurrency);
    const amount = getValues('amount');
    const { formattedAmount } = formatAmountWithDecimals({
      amount,
      lang: currentLanguage,
      withDecimals: selectedCurrency.supportsDecimals,
    });
    handleAmountChange({
      field: amountField,
      value: formattedAmount,
    });
  };

  const hasErrors = formState.errors.amount || formState.errors.currency;

  return (
    <Box
      as="form"
      display="flex"
      flexDir="column"
      height="100%"
      justifyContent="space-between"
      onSubmit={handleSubmit((values) => {
        if (!quote) {
          return;
        }
        onSubmit({
          currency: values.currency,
          quote,
        });
      })}
    >
      <VStack alignItems="stretch" spacing="space-24">
        <Form.Field
          error={
            formState.errors.amount?.message ||
            formState.errors.currency?.message
          }
          label={locales.amountLabel}
        >
          <VStack alignItems="stretch" spacing="space-4">
            <Controller
              control={control}
              name="amount"
              render={({ field: amountField }) => (
                <InputGroup ref={amountInputRef} zIndex="modal">
                  <Form.Input
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    data-testid="international-transfer-amount-input"
                    onBlur={() => handleAmountBlur(amountField)}
                    onChange={(event) =>
                      handleAmountChange({
                        field: amountField,
                        value: event.target.value,
                      })
                    }
                    type="text"
                    value={amountField.value}
                  />
                  <InputRightElement paddingRight="space-6">
                    <Form.Field gap={0}>
                      <Controller
                        control={control}
                        name="currency"
                        render={({ field: currencyField }) => {
                          return (
                            <CurrencyInput
                              containerWidth={width}
                              currencies={currencies}
                              onCurrencyChange={(selectedCurrency) =>
                                handleCurrencyChange({
                                  amountField,
                                  currencyField,
                                  selectedCurrency,
                                })
                              }
                              selectedCurrency={currencyField.value}
                            />
                          );
                        }}
                      />
                    </Form.Field>
                  </InputRightElement>
                </InputGroup>
              )}
            />
            {isCreatingQuote ? (
              <Skeleton
                borderRadius="radius-24"
                endColor="grey.300"
                height="8px"
                marginTop="space-4"
                startColor="grey.100"
                width="112px"
              />
            ) : null}
            {!isCreatingQuote && quote && !hasErrors ? (
              <Text fontSize="font-12" variant="secondary">
                {locales.formatString(locales.transferFees, {
                  fees: formatAmount({
                    currency: quote.fee.currency,
                    forcePlusSign: false,
                    value: quote.fee.amountFloat,
                  }),
                  sourceAmount: formatAmount({
                    currency: quote.source.currency,
                    forcePlusSign: false,
                    value: quote.source.amountFloat,
                  }),
                })}
              </Text>
            ) : null}
          </VStack>
        </Form.Field>
        {(quote || isCreatingQuote) && !hasErrors ? (
          <TransferTypeCard
            description={
              quote?.formattedEstimatedDeliveryAt
                ? locales.formatString(locales.transferTypeCardDescription, {
                    estimatedReceptionDate: quote.formattedEstimatedDeliveryAt,
                  })
                : undefined
            }
            isLoading={isCreatingQuote}
            type="international"
          />
        ) : null}
      </VStack>
      <Layout.FooterContainer>
        <Divider />
        <Layout.FooterButtonsContainer>
          <Button
            icon="arrow-right"
            iconPosition="right"
            isDisabled={!quote || isCreatingQuote}
            type="submit"
          >
            {locales.chooseBeneficiaryCTA}
          </Button>
        </Layout.FooterButtonsContainer>
      </Layout.FooterContainer>
    </Box>
  );
};

export const InternationalTransferAmountAndCurrencySelection: FC<
  SelectAmountAndCurrencyProps
> = ({ goBack, goNext }) => {
  const {
    bankTransfer: { fromBankAccountId },
    setBankTransfer,
  } = useBankTransferContext('selectAmountAndCurrency');
  const {
    error: internationalTransferCurrenciesError,
    internationalTransferCurrencies,
    loading: currenciesLoading,
  } = useInternationalTransferCurrencies();
  const {
    data: bankTransferData,
    error: bankTransferDataError,
    loading: bankTransferDataLoading,
  } = useBankTransferData({
    fromBankAccountId,
  });

  const handleSubmit = ({
    currency,
    quote,
  }: {
    quote: UseInternationalTransferQuoteReturnType['quote'];
    currency: Currency;
  }) => {
    setBankTransfer((prevState) => ({
      ...prevState,
      currency,
      quote: quote || undefined,
    }));
    goNext();
  };

  if (
    bankTransferDataError ||
    internationalTransferCurrenciesError ||
    (internationalTransferCurrencies &&
      internationalTransferCurrencies?.length === 0)
  ) {
    return <ErrorView />;
  }

  return (
    <Layout.Root>
      <Layout.Body>
        <TrackView pageName="InternationalTransferAmountAndCurrencySelection" />
        <NavigationHeader onBack={goBack} />
        <ContentLayout.Header title={locales.toAccount} />
        {currenciesLoading ||
        !internationalTransferCurrencies ||
        bankTransferDataLoading ? (
          <Loader />
        ) : (
          <AmountAndCurrencyForm
            currencies={internationalTransferCurrencies}
            nextBalanceFloat={
              bankTransferData.fromBankAccount.nextBalance / 100
            }
            onSubmit={handleSubmit}
          />
        )}
      </Layout.Body>
    </Layout.Root>
  );
};
